/**
 * Slim Uren-leerlus (okt 2026): de kwaliteitsmeter en je eigen geleerde regels.
 *
 * - KwaliteitTegel: per week hoeveel voorstellen je ongewijzigd schreef, hoe vaak een klantafspraak op het
 *   klantproject stond (recall), de urenafwijking en hoeveel uren je zelf moest toevoegen. Doelen: ≥ 70%
 *   ongewijzigd, extern-recall ≥ 80%. Bron: de nachtelijke vergelijking voorstel ↔ Gripp.
 * - MijnRegels: wat Slim Uren van jou heeft geleerd (titel → regel + uren, overslaan, deelnemers → project,
 *   aanvulling). Uitzetten met "Ongedaan maken"; uren aanpassen maakt een regel handmatig (blijft staan).
 * Alleen je eigen gegevens: de backend neemt het e-mailadres uit je login.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Chip, Switch, TextField, Tooltip, CircularProgress } from '@mui/material';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { leesJson, meldFout } from '../../utils/apiFout';
import { biedOngedaanAan } from '../../utils/ongedaan';

const pct = (v) => (v === null || v === undefined ? '–' : `${Math.round(v)}%`);

function Meter({ label, waarde, doel, omlaagIsGoed = false, uitleg }) {
    const gehaald = doel !== undefined && waarde !== null && waarde !== undefined
        && (omlaagIsGoed ? waarde <= doel : waarde >= doel);
    return (
        <Tooltip title={uitleg || ''}>
            <Box sx={{ minWidth: 110 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>{label}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: gehaald ? '#166534' : 'text.primary', lineHeight: 1.3 }}>
                    {pct(waarde)}
                    {doel !== undefined && (
                        <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 0.75, fontWeight: 400 }}>
                            doel {omlaagIsGoed ? '≤' : '≥'} {doel}%
                        </Typography>
                    )}
                </Typography>
            </Box>
        </Tooltip>
    );
}

export function KwaliteitTegel() {
    const [data, setData] = useState(null);
    useEffect(() => {
        let actief = true;
        authFetch(`${BACKEND_URL}/Api/slim-uren/kwaliteit?weken=8`)
            .then((r) => leesJson(r, 'Kwaliteit laden'))
            .then((d) => { if (actief) setData(d); })
            .catch(() => { if (actief) setData({ weken: [] }); }); // bewust stil: de meter is extra, het voorstel werkt zonder
        return () => { actief = false; };
    }, []);
    if (!data) return null;
    const weken = data.weken || [];
    const laatste = weken[weken.length - 1];
    const doel = data.doel || { ongewijzigd_pct: 70, extern_recall_pct: 80 };
    return (
        <Paper variant="outlined" sx={{ px: 2, py: 1.25, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                    Hoe goed was het voorstel? {laatste ? `(week van ${laatste.week.slice(5).split('-').reverse().join('-')})` : ''}
                </Typography>
                {weken.length > 1 && (
                    <Tooltip title={`% ongewijzigd per week: ${weken.map((w) => pct(w.ongewijzigd_pct)).join(' → ')}`}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 22 }} aria-label="Trend ongewijzigd per week">
                            {weken.map((w) => (
                                <Box key={w.week} sx={{ width: 6, borderRadius: 0.5, height: `${Math.max(2, (w.ongewijzigd_pct || 0) / 100 * 22)}px`,
                                    bgcolor: (w.ongewijzigd_pct || 0) >= doel.ongewijzigd_pct ? '#86efac' : '#cbd5e1' }} />
                            ))}
                        </Box>
                    </Tooltip>
                )}
            </Box>
            {!laatste ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Nog geen vergeleken dagen. Elke nacht legt Slim Uren je voorstel naast wat je in Gripp schreef; daarna verschijnt hier de meter.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Meter label="Ongewijzigd" waarde={laatste.ongewijzigd_pct} doel={doel.ongewijzigd_pct}
                        uitleg="Voorgestelde regels die je precies zo schreef (zelfde regel, zelfde uren)." />
                    <Meter label="Klant herkend" waarde={laatste.extern_recall_pct} doel={doel.extern_recall_pct}
                        uitleg="Van je regels op een klantproject: hoe vaak stond die al op dat project in het voorstel." />
                    <Meter label="Klant juist" waarde={laatste.extern_precisie_pct}
                        uitleg="Van de voorstellen op een klantproject: hoe vaak dat het goede project was." />
                    <Meter label="Urenafwijking" waarde={laatste.uren_afwijking_pct} omlaagIsGoed
                        uitleg="Verschil tussen voorgestelde en geschreven uren, als deel van de voorgestelde uren." />
                    <Meter label="Zelf toegevoegd" waarde={laatste.toegevoegd_uren_pct} omlaagIsGoed
                        uitleg="Deel van je geschreven uren dat niet in het voorstel stond." />
                </Box>
            )}
        </Paper>
    );
}

const SOORT = { titel: 'Afspraak', woord: 'Woord', domein: 'Deelnemers', aanvulling: 'Aanvulling', factor: 'Duur' };

function omschrijf(r) {
    const doel = r.regel_naam || r.project_naam || r.project;
    if (r.actie === 'overslaan') return `“${r.titel || r.sleutel}” → overslaan (geen uren)`;
    if (r.soort === 'aanvulling') return `dag aanvullen met ${doel}`;
    if (r.soort === 'factor') return `${doel}: ${r.uren}× de agendaduur bij korte afspraken`;
    if (r.soort === 'domein') return `afspraken met @${r.sleutel} → ${r.project_naam || r.project}`;
    if (r.soort === 'woord') return `“${r.sleutel}” in de titel → ${doel}`;
    return `“${r.titel || r.sleutel}” → ${doel}`;
}

export function MijnRegels() {
    const [regels, setRegels] = useState(null);
    const laad = useCallback(() => {
        authFetch(`${BACKEND_URL}/Api/slim-uren/mijn-regels`)
            .then((r) => leesJson(r, 'Regels laden'))
            .then((d) => setRegels(d.regels || []))
            .catch((e) => { meldFout(e, 'Regels laden'); setRegels([]); });
    }, []);
    useEffect(() => { laad(); }, [laad]);

    const wijzig = useCallback(async (regel, velden) => {
        const r = await authFetch(`${BACKEND_URL}/Api/slim-uren/mijn-regels/${regel.id}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden),
        });
        const d = await leesJson(r, 'Regel aanpassen');
        setRegels((prev) => (prev || []).map((x) => (x.id === regel.id ? d.regel : x)));
        return d.regel;
    }, []);

    const zetAanUit = async (regel) => {
        const uit = !regel.uitgeschakeld;
        try {
            await wijzig(regel, { uitgeschakeld: uit });
            if (uit) {
                biedOngedaanAan('Regel uitgezet', () => wijzig(regel, { uitgeschakeld: false }), { eigenKnop: true });
            }
        } catch (e) { meldFout(e, 'Regel aanpassen'); }
    };

    const zetUren = async (regel, waarde) => {
        const uren = waarde === '' ? null : Number(String(waarde).replace(',', '.'));
        if (uren !== null && !(uren > 0 && uren <= 12)) return;
        if (uren === regel.uren) return;
        const oud = regel.uren;
        try {
            await wijzig(regel, { uren });
            biedOngedaanAan(`Uren aangepast naar ${uren ?? 'agendaduur'}`, () => wijzig(regel, { uren: oud }), { eigenKnop: true });
        } catch (e) { meldFout(e, 'Regel aanpassen'); }
    };

    if (regels === null) return <CircularProgress size={18} />;
    if (!regels.length) {
        return (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Nog geen geleerde regels. Slim Uren leert elke nacht van wat je in Gripp schreef: een afspraak die je twee keer
                op hetzelfde project en dezelfde regel zet, wordt een regel.
            </Typography>
        );
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {regels.map((r) => (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, opacity: r.uitgeschakeld ? 0.5 : 1 }}>
                    <Switch size="small" checked={!r.uitgeschakeld} onChange={() => zetAanUit(r)}
                        inputProps={{ 'aria-label': r.uitgeschakeld ? 'Regel aanzetten' : 'Regel uitzetten' }} />
                    <Chip size="small" label={SOORT[r.soort] || r.soort} variant="outlined" sx={{ height: 20, fontSize: 10, minWidth: 76 }} />
                    <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0 }} noWrap title={omschrijf(r)}>{omschrijf(r)}</Typography>
                    {r.actie === 'boek' && r.soort !== 'factor' && r.soort !== 'domein' && r.soort !== 'woord' && (
                        <TextField size="small" defaultValue={r.uren ?? ''} placeholder="duur"
                            onBlur={(e) => zetUren(r, e.target.value)}
                            inputProps={{ 'aria-label': 'Uren', style: { width: 44, fontSize: 12, padding: '2px 6px' } }} />
                    )}
                    <Tooltip title={`${r.aantal} van de laatste ${r.van || r.aantal} keer${r.bron === 'handmatig' ? ' · door jou aangepast' : ''}`}>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', width: 34, textAlign: 'right' }}>{r.aantal}×</Typography>
                    </Tooltip>
                </Box>
            ))}
        </Box>
    );
}
